import PropTypes from "prop-types";
import React, { useRef } from "react";

import { classNames } from "@swa-ui/string";

import { Anchor } from "../Anchor";
import { AnchorTarget } from "../AnchorTarget";
import { CaptionPointer } from "../CaptionPointer";
import { FocusContainer } from "../FocusContainer";
import { Shadow } from "../Shadow";
import { Transition } from "../Transition";
import { Width, widthPropTypes } from "../Width";
import styles from "./Flyout.module.scss";

const ANIMATION_DELAY_HIDDEN = 0.1;
const CAPTION_POINTER_SIZE = 24;
const DURATION_FAST = 0.1;
const DURATION_NORMAL = 0.3;
const DURATION_SLOW = 0.35;
const FILTER_MAXIMUM = "2px";
const FILTER_NONE = "0px";
const OFFSET_FOR_BORDERS = 2;
const OPAQUE = 1;
const POSITION_END = 0;
const POSITION_START = 2;
const ROTATION_MEDIUM = 12;
const ROTATION_NONE = 0;
const TRANSFORM_ORIGIN_CENTER = "50%";
const TRANSFORM_ORIGIN_NEAR_END = "75%";
const TRANSFORM_ORIGIN_NEAR_START = "25%";
const TRANSPARENT = 0;

/**
 * Flyout provides a way to display an element adjacent to another element ("anchor") to appear as a
 * top level "overlay". See the location propType description for an important note.
 *
 * NOTE:
 * classNameAnchor and classNameFlyout must be given.
 * classNameAnchor must contain an anchor-name property and classNameFlyout contain a position-anchor
 * property. These should be something like:
 *
 * .classNameAnchor {
 *   anchor-name: --my-anchor;
 * }
 *
 * .classNameFlyout {
 *   position-anchor: --my-anchor;
 * }
 *
 * The name given to these properties, '--my-anchor' in the example above, must match. See Anchor
 * and AnchorTarget for info.
 *
 * TODO:
 * The classNameAnchor and classNameFlyout requirements are temporary until browser support is
 * widely implemented. These two props can then go away with PHX-2817.
 */

export const Flyout = (props) => {
  const {
    allowOverflow = false,
    children,
    className,
    classNameAnchor,
    classNameFlyout,
    flyout,
    location = "hidden",
    name,
    pointerAlignment = "center",
    pointerXOffset = OFFSET_FOR_BORDERS,
    showPointer = true,
    width,
  } = props;
  const [currentLocation, setCurrentLocation] = React.useState("hidden");
  const containerRef = useRef();

  return (
    <div className={classNames(styles.flyout, className)} ref={containerRef}>
      <Anchor {...getAnchorProps()}>{children}</Anchor>
      {renderFlyout()}
      {renderPointer()}
    </div>
  );

  function renderFlyout() {
    return (
      <AnchorTarget {...getFlyoutProps()}>
        <Transition {...getTransitionProps()}>
          <Shadow>
            <Width width={width}>
              <FocusContainer {...getFocusContainerProps()}>{renderContent()}</FocusContainer>
            </Width>
          </Shadow>
        </Transition>
      </AnchorTarget>
    );
  }

  function renderPointer() {
    return showPointer ? (
      <AnchorTarget {...getPointerProps()}>
        <Transition {...getPointerTransitionProps()}>
          <CaptionPointer {...getCaptionPointerProps()} />
        </Transition>
      </AnchorTarget>
    ) : null;
  }

  function renderContent() {
    return <div {...getFlyoutContentProps()}>{flyout}</div>;
  }

  function getFlyoutContentProps() {
    return {
      "aria-describedby": props["aria-describedby"],
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      ...(props["aria-label"] ||
        (props["aria-labelledby"] && {
          "aria-modal": "true",
          role: "dialog",
        })),
    };
  }

  function getAnchorProps() {
    return {
      className: classNames(styles.anchor, classNameAnchor),
      name,
    };
  }

  function getTransitionProps() {
    return {
      animate: getAnimationValues(),
      className: getAnimationClass(),
      onTransformationEnd: handleTransformationEnd,
      style: { transformOrigin: getTransformOrigin() },
      transition: getTransition(),
    };
  }

  function getFocusContainerProps() {
    return {
      "aria-hidden": props["aria-hidden"] || currentLocation === "hidden",
      revealed: currentLocation !== "hidden",
    };
  }

  function getPointerTransitionProps() {
    return {
      animate: { opacity: getOpacity() },
      transition: getPointerTransition(),
    };
  }

  function getFlyoutProps() {
    return {
      className: classNames(styles.target, classNameFlyout, {
        [styles[location]]: showPointer && location !== "hidden",
        [styles[currentLocation]]: showPointer && currentLocation !== "hidden",
      }),
      location: getLocation(),
      name,
      ...(allowOverflow && { transform: getOffsetToCenter() }),
    };
  }

  function getCaptionPointerProps() {
    const aboveOrBelow = isAboveOrBelow();

    return {
      className: classNames({
        [styles.alignmentAboveLeft]: pointerAlignment === "left" && location === "above",
        [styles.alignmentAboveRight]: pointerAlignment === "right" && location === "above",
        [styles.alignmentLeft]: pointerAlignment === "left" && aboveOrBelow,
        [styles.alignmentRight]: pointerAlignment === "right" && aboveOrBelow,
      }),
      direction: getPointerDirection(),
      ...(pointerAlignment === "left" && { style: { left: pointerXOffset } }),
      ...(pointerAlignment === "right" && { style: { right: pointerXOffset } }),
    };
  }

  function getPointerProps() {
    return {
      className: classNameFlyout,
      location:
        (pointerAlignment === "left" || pointerAlignment === "right") && isAboveOrBelow()
          ? `${location} ${pointerAlignment}`
          : getLocation(),
      name,
      ...(isPointerTransformNeeded() && { transform: getOffsetToCenter() }),
    };
  }

  function getAnimationClass() {
    return classNames(styles.transition, {
      [styles.hidden]: currentLocation === "hidden" && location === "hidden",
    });
  }

  function handleTransformationEnd() {
    setCurrentLocation(location);
  }

  function getAnimationValues() {
    return {
      filter: getFilter(),
      opacity: getOpacity(),
      rotateX: getXRotation(),
      rotateY: getYRotation(),
      x: getX(),
      y: getY(),
    };
  }
  function getFilter() {
    return location === "hidden"
      ? [`blur(${FILTER_NONE})`, `blur(${FILTER_MAXIMUM})`]
      : [`blur(${FILTER_MAXIMUM})`, `blur(${FILTER_NONE})`];
  }

  function getOpacity() {
    return location === "hidden" ? [OPAQUE, TRANSPARENT] : [TRANSPARENT, OPAQUE];
  }

  function getXRotation() {
    return location === "above"
      ? [ROTATION_MEDIUM, ROTATION_NONE]
      : location === "below"
      ? [-ROTATION_MEDIUM, ROTATION_NONE]
      : [ROTATION_NONE, ROTATION_NONE];
  }

  function getYRotation() {
    return location === "left"
      ? [-ROTATION_MEDIUM, ROTATION_NONE]
      : location === "right"
      ? [ROTATION_MEDIUM, ROTATION_NONE]
      : [ROTATION_NONE, ROTATION_NONE];
  }

  function getTransformOrigin() {
    const origins = {
      above: `${TRANSFORM_ORIGIN_NEAR_END} ${TRANSFORM_ORIGIN_CENTER}`,
      below: `${TRANSFORM_ORIGIN_NEAR_START} ${TRANSFORM_ORIGIN_CENTER}`,
      hidden: `${TRANSFORM_ORIGIN_CENTER} ${TRANSFORM_ORIGIN_CENTER}`,
      left: `${TRANSFORM_ORIGIN_NEAR_END} ${TRANSFORM_ORIGIN_CENTER}`,
      right: `${TRANSFORM_ORIGIN_NEAR_START} ${TRANSFORM_ORIGIN_CENTER}`,
    };

    return origins[location];
  }

  function getX() {
    return location === "left"
      ? [POSITION_START, POSITION_END]
      : location === "right"
      ? [-POSITION_START, POSITION_END]
      : [POSITION_END, POSITION_END];
  }

  function getY() {
    return location === "above"
      ? [POSITION_START, POSITION_END]
      : location === "below"
      ? [-POSITION_START, POSITION_END]
      : [POSITION_END, POSITION_END];
  }

  function getTransition() {
    return {
      duration: location === "hidden" ? DURATION_FAST : DURATION_NORMAL,
      ease: "easeIn",
    };
  }

  function getPointerTransition() {
    return {
      delay: location === "hidden" ? 0 : ANIMATION_DELAY_HIDDEN,
      duration: location === "hidden" ? DURATION_FAST : DURATION_SLOW,
      ease: "easeOut",
    };
  }

  function getPointerDirection() {
    const directions = {
      above: "down",
      below: "up",
      left: "right",
      right: "left",
    };

    return directions[getLocation()];
  }

  function getOffsetToCenter() {
    const { height: containerHeight, width: containerWidth } =
      containerRef.current?.getBoundingClientRect() ?? {
        height: 0,
        width: 0,
      };

    return isAboveOrBelow()
      ? `translateX(calc(${containerWidth / 2}px - ${TRANSFORM_ORIGIN_CENTER}))`
      : `translateY(calc(${containerHeight / 2}px - ${TRANSFORM_ORIGIN_CENTER}))`;
  }

  function isAboveOrBelow() {
    const place = getLocation();

    return place === "above" || place === "below";
  }

  function isPointerTransformNeeded() {
    const { height: containerHeight, width: containerWidth } =
      containerRef.current?.getBoundingClientRect() ?? {
        height: 0,
        width: 0,
      };
    const place = getLocation();

    return (
      ((place === "above" || place === "below") && containerWidth < CAPTION_POINTER_SIZE) ||
      ((place === "left" || place === "right") && containerHeight < CAPTION_POINTER_SIZE)
    );
  }

  function getLocation() {
    return location === "hidden" ? currentLocation : location;
  }
};

export const flyoutPropTypes = {
  /**
   * Flyout, by default, will not exceed its container (nearest non-static parent). If this
   * is desired, set allowOverflow to true. This applies to the "opposite" direction than the flyout.
   * That is, allowOverflow will affect the horizontal placement of the flyout when location is
   * above or below, or the flyout's vertical placement if location is left or right.
   */
  allowOverflow: PropTypes.bool,

  /** aria-describedby text to provide accessibility description for the flyout. */
  "aria-describedby": PropTypes.string,

  /** aria-label text to provide accessibility description for the flyout. */
  "aria-label": PropTypes.string,

  /** aria-labelledby text to provide accessibility label for the flyout. */
  "aria-labelledby": PropTypes.string,

  /** Content that will be rendered. */
  children: PropTypes.node,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /**
   * Additional classes for positioning the component. It must contain a anchor-name property.
   * Something like:
   * anchor-name: --my-anchor;
   */
  classNameAnchor: PropTypes.string.isRequired,

  /**
   * Additional classes for positioning the component. It must contain a position-anchor property.
   * Something like:
   * position-anchor: --my-anchor;
   */
  classNameFlyout: PropTypes.string.isRequired,

  /** Content to be render inside flyout when location is not "hidden". */
  flyout: PropTypes.node,

  /**
   * Location indicates which adjacent side to render target relative to anchor. When placing the
   * flyout (setting the location to anything but hidden), location must be set to hidden first. For
   * example, if the location is set to "above", then it's changed to "left", there may be a flash
   * during the rendering in the new location. Always set to hidden before repositioning to avoid
   * this flash.
   */
  location: PropTypes.oneOf(["above", "below", "hidden", "left", "right"]),

  /**
   * Name that will be used to tie Anchor and Flyout together. Both Anchor and Flyout must
   * receive the same name prop value.
   */
  name: PropTypes.string.isRequired,

  /**
   * Alignment here refers to the alignment relative to the anchor (children). The pointer's
   * alignment can only be set when location is "above" or "below".
   */
  pointerAlignment: PropTypes.oneOf(["center", "left", "right"]),

  /** Positive or negative value to nudge the pointer left or right. */
  pointerXOffset: PropTypes.number,

  /** True or false to define if the caret will be displayed between children (anchor) and flyout. */
  showPointer: PropTypes.bool,

  /** Width of content container. */
  width: widthPropTypes.width,
};

Flyout.propTypes = flyoutPropTypes;
